import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import authService from '../services/authService';
import { ADMIN_EMAIL } from '../../../config';

export const useLogin = () => {
  const location = useLocation();

  // State management
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [needsVerification, setNeedsVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [rememberMe, setRememberMe] = useState(false);

  // Initialize remember me
  useEffect(() => {
    const savedUsername = localStorage.getItem('rememberUsername');
    if (savedUsername) {
      setFormData(prev => ({ ...prev, username: savedUsername }));
      setRememberMe(true);
    }
  }, [location]);

  // Handle login submission
  const handleSubmit = async (onLoginSuccess) => {
    setError("");

    if (!formData.username || !formData.password) {
      setError("Please enter your username and password.");
      return;
    }

    setIsLoading(true);

    try {
      const result = await authService.signIn(formData.username, formData.password);

      if (result.success) {
        const userInfo = { ...result.user };

        // Store user information
        localStorage.setItem('userInfo', JSON.stringify(userInfo));
        localStorage.setItem('authToken', result.tokens.accessToken);
        localStorage.setItem('idToken', result.tokens.idToken);
        localStorage.setItem('refreshToken', result.tokens.refreshToken);
        localStorage.setItem('isLoggedIn', 'true');

        // Determine user role
        let userRole = 'user';

        if (formData.username === ADMIN_EMAIL || result.user.email === ADMIN_EMAIL) {
          console.log('Admin email matched:', ADMIN_EMAIL);
          userRole = 'admin';
        }

        console.log('Final userRole:', userRole);
        localStorage.setItem('userRole', userRole);

        // Handle Remember Me
        if (rememberMe) {
          localStorage.setItem('rememberUsername', formData.username);
        } else {
          localStorage.removeItem('rememberUsername');
        }

        // Update header
        window.dispatchEvent(new CustomEvent('userInfoUpdated'));

        // Login success callback
        if (onLoginSuccess) {
          onLoginSuccess(userRole);
        }
      } else {
        if (result.needsNewPassword) {
          setError("A new password is required. Please contact your administrator.");
        } else {
          setError(result.error || "Login failed. Please try again.");
        }
      }
    } catch (err) {
      console.error("로그인 오류:", err);

      if (err.name === 'UserNotConfirmedException' || err.message?.includes('not confirmed')) {
        setNeedsVerification(true);
        setError("Email verification required. Please enter your verification code.");
      } else {
        setError(authService.getErrorMessage(err) || "An error occurred during login.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle verification
  const handleVerification = async (onLoginSuccess) => {
    setError("");

    if (!verificationCode || verificationCode.length !== 6) {
      setError("Please enter the 6-digit verification code.");
      return;
    }

    setIsLoading(true);

    try {
      const result = await authService.confirmSignUp(formData.username, verificationCode);

      if (result.success) {
        // Auto login after verification
        const loginResult = await authService.signIn(formData.username, formData.password);

        if (loginResult.success) {
          const userInfo = { ...loginResult.user };

          localStorage.setItem('userInfo', JSON.stringify(userInfo));
          localStorage.setItem('authToken', loginResult.tokens.accessToken);
          localStorage.setItem('idToken', loginResult.tokens.idToken);
          localStorage.setItem('refreshToken', loginResult.tokens.refreshToken);
          localStorage.setItem('isLoggedIn', 'true');

          let userRole = 'user';
          if (formData.username === ADMIN_EMAIL || loginResult.user.email === ADMIN_EMAIL) {
            userRole = 'admin';
          }
          localStorage.setItem('userRole', userRole);

          window.dispatchEvent(new CustomEvent('userInfoUpdated'));

          if (onLoginSuccess) {
            onLoginSuccess(userRole);
          }
        } else {
          setError("Verification succeeded but login failed. Please try again.");
          setNeedsVerification(false);
        }
      } else {
        setError(result.error || "Verification failed. Please try again.");
      }
    } catch (err) {
      console.error("Verification error:", err);
      setError("An error occurred during verification.");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle resend code
  const handleResendCode = async () => {
    setError("");
    setIsLoading(true);

    try {
      const result = await authService.resendConfirmationCode(formData.username);

      if (result.success) {
        alert(result.message || "Verification code has been resent.");
      } else {
        setError(result.error || "Failed to resend verification code.");
      }
    } catch (err) {
      console.error("Resend code error:", err);
      setError("An error occurred while resending the verification code.");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle input change
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setError("");
  };

  const [forgotStep, setForgotStep] = useState(null); // null | 'email' | 'reset'
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotCode, setForgotCode] = useState('');
  const [forgotNewPassword, setForgotNewPassword] = useState('');

  const handleForgotPassword = () => {
    setForgotStep('email');
    setError('');
  };

  const handleForgotSubmitEmail = async () => {
    if (!forgotEmail) { setError('Please enter your email.'); return; }
    setIsLoading(true);
    setError('');
    try {
      const result = await authService.forgotPassword(forgotEmail);
      if (result.success) {
        setForgotStep('reset');
      } else {
        setError(result.error || 'Failed to send reset code.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotConfirm = async (onLoginSuccess) => {
    if (!forgotCode || forgotCode.length !== 6) { setError('Please enter the 6-digit code.'); return; }
    if (!forgotNewPassword) { setError('Please enter a new password.'); return; }
    setIsLoading(true);
    setError('');
    try {
      const result = await authService.confirmForgotPassword(forgotEmail, forgotCode, forgotNewPassword);
      if (result.success) {
        setForgotStep(null);
        setError('');
        alert('Password reset successfully. Please sign in.');
      } else {
        setError(result.error || 'Failed to reset password.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelForgot = () => {
    setForgotStep(null);
    setForgotEmail('');
    setForgotCode('');
    setForgotNewPassword('');
    setError('');
  };

  return {
    // State
    formData,
    isLoading,
    error,
    needsVerification,
    verificationCode,
    rememberMe,
    forgotStep,
    forgotEmail,
    forgotCode,
    forgotNewPassword,

    // Actions
    handleSubmit,
    handleVerification,
    handleResendCode,
    handleInputChange,
    handleForgotPassword,
    handleForgotSubmitEmail,
    handleForgotConfirm,
    handleCancelForgot,
    setForgotEmail,
    setForgotCode,
    setForgotNewPassword,
    setVerificationCode,
    setRememberMe,
    setNeedsVerification,
  };
};
